import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api, API, downloadPdf } from "@/lib/api";
import { toast } from "sonner";
import {
  HeartPulse, Search, Plus, Trash2, Weight, Thermometer, Ruler, X, Loader2,
  FileCheck2, AlertTriangle, Stethoscope, User, CalendarDays, Image as ImageIcon, Paperclip,
  Download, Save, MessageCircle, HeartHandshake,
} from "lucide-react";

const groupLabel = (v) => (v && v.includes("|") ? v.split("|").join(" · ") : v || "—");
const MEDICAL_FIELDS = [
  { k: "blood_type", label: "Tipo de sangre" },
  { k: "allergies", label: "Alergias" },
  { k: "chronic", label: "Enfermedades / padecimientos crónicos" },
  { k: "disability", label: "Discapacidad" },
  { k: "medications", label: "Medicamentos habituales" },
  { k: "insurance", label: "Seguro médico / póliza" },
  { k: "emergency_name", label: "Contacto de emergencia" },
  { k: "emergency_phone", label: "Tel. de emergencia" },
  { k: "clothing_size", label: "Talla de ropa" },
  { k: "notes", label: "Observaciones" },
];
const PERIODS = [
  { id: "all", label: "Todo" }, { id: "day", label: "Día" }, { id: "week", label: "Semana" },
  { id: "month", label: "Mes" }, { id: "year", label: "Año" },
];
const KIND_META = {
  enfermeria: { label: "Enfermería", icon: Stethoscope, cls: "bg-rose-100 text-rose-700 border-rose-200" },
  incidencia: { label: "Bitácora", icon: AlertTriangle, cls: "bg-amber-100 text-amber-700 border-amber-200" },
  justificante: { label: "Justificante", icon: FileCheck2, cls: "bg-sky-100 text-sky-700 border-sky-200" },
};
const todayStr = () => new Date().toISOString().slice(0, 10);

export default function Enfermeria({ forceView }) {
  const [viewState, setView] = useState("pesos"); // pesos | actividad
  const view = forceView || viewState;
  const [students, setStudents] = useState([]);
  const [q, setQ] = useState("");
  const [group, setGroup] = useState("");
  const [period, setPeriod] = useState("all");
  const [anchor, setAnchor] = useState(todayStr());
  const [pesos, setPesos] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [showForm, setShowForm] = useState(null); // student obj or {}
  const [expediente, setExpediente] = useState(null); // student_id

  const params = useMemo(() => {
    const p = {};
    if (q.trim()) p.q = q.trim();
    if (group) p.group = group;
    if (period && period !== "all") { p.period = period; p.anchor = anchor; }
    return p;
  }, [q, group, period, anchor]);

  const groups = useMemo(() => {
    const s = new Set();
    students.forEach((x) => { const g = x.group || x.grade; if (g) s.add(g); });
    return [...s].sort();
  }, [students]);

  useEffect(() => {
    api.get("/students", { params: { kind: "student" } })
      .then((r) => setStudents(Array.isArray(r.data) ? r.data : (r.data.students || [])))
      .catch(() => setStudents([]));
  }, []);

  const loadPesos = useCallback(() => {
    setPesos(null);
    const p = {}; if (q.trim()) p.q = q.trim(); if (group) p.group = group;
    api.get("/enfermeria/pesos", { params: p }).then(({ data }) => setPesos(data.students || [])).catch(() => setPesos([]));
  }, [q, group]);

  const loadTimeline = useCallback(() => {
    setTimeline(null);
    api.get("/enfermeria/timeline", { params }).then(({ data }) => setTimeline(data.items || [])).catch(() => setTimeline([]));
  }, [params]);

  useEffect(() => { if (view === "pesos") loadPesos(); else loadTimeline(); }, [view, loadPesos, loadTimeline]);

  const reload = () => { loadPesos(); if (view === "actividad") loadTimeline(); };

  return (
    <div data-testid="enfermeria-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="font-display font-bold text-lg text-navy flex items-center gap-2"><HeartPulse className="w-5 h-5 text-rose-500" /> Enfermería</h2>
          <p className="text-xs text-nardo-500">Expediente médico del alumno: peso, atenciones, bitácora de docentes y justificantes médicos.</p>
        </div>
        <button onClick={() => setShowForm({})} data-testid="enf-new-btn" className="inline-flex items-center gap-2 bg-rose-500 hover:bg-rose-600 text-white px-4 py-2.5 rounded-lg text-sm font-semibold shadow"><Plus className="w-4 h-4" /> Registrar atención</button>
      </div>

      {/* Filtros */}
      <div className="bg-white border border-nardo-200 rounded-xl p-3 mb-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[180px]">
          <label className="text-[10px] uppercase tracking-wide text-nardo-500 block mb-1">Buscar alumno</label>
          <div className="relative"><Search className="w-4 h-4 text-nardo-400 absolute left-2.5 top-2.5" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nombre del alumno" data-testid="enf-search" className="w-full border border-nardo-200 rounded-lg pl-8 pr-3 py-2 text-sm text-navy outline-none focus:border-navy" /></div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wide text-nardo-500 block mb-1">Grupo</label>
          <select value={group} onChange={(e) => setGroup(e.target.value)} data-testid="enf-group" className="border border-nardo-200 rounded-lg px-3 py-2 text-sm text-navy bg-white outline-none focus:border-navy min-w-[150px]">
            <option value="">Todos los grupos</option>
            {groups.map((g) => <option key={g} value={g}>{groupLabel(g)}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wide text-nardo-500 block mb-1">Periodo</label>
          <div className="flex gap-1">
            {PERIODS.map((p) => (
              <button key={p.id} onClick={() => setPeriod(p.id)} data-testid={`enf-period-${p.id}`} className={`text-[11px] px-2.5 py-2 rounded-lg transition-colors ${period === p.id ? "bg-navy text-white" : "bg-white text-nardo-600 border border-nardo-200 hover:border-navy"}`}>{p.label}</button>
            ))}
          </div>
        </div>
        {period !== "all" && (
          <div><label className="text-[10px] uppercase tracking-wide text-nardo-500 block mb-1">Fecha</label>
            <input type="date" value={anchor} onChange={(e) => setAnchor(e.target.value)} data-testid="enf-anchor" className="border border-nardo-200 rounded-lg px-3 py-2 text-sm text-navy outline-none focus:border-navy" /></div>
        )}
      </div>

      {/* Toggle vista */}
      <div className={`${forceView ? "hidden" : "inline-flex"} rounded-lg border border-nardo-200 overflow-hidden mb-4`}>
        <button onClick={() => setView("pesos")} data-testid="enf-view-pesos" className={`px-4 py-2 text-sm font-semibold inline-flex items-center gap-1.5 ${view === "pesos" ? "bg-navy text-white" : "text-nardo-600 hover:bg-nardo-50"}`}><Weight className="w-4 h-4" /> Peso de alumnos</button>
        <button onClick={() => setView("actividad")} data-testid="enf-view-actividad" className={`px-4 py-2 text-sm font-semibold inline-flex items-center gap-1.5 ${view === "actividad" ? "bg-navy text-white" : "text-nardo-600 hover:bg-nardo-50"}`}><CalendarDays className="w-4 h-4" /> Actividad y expediente</button>
      </div>

      {view === "pesos" ? (
        <PesosTable pesos={pesos} onRegister={(s) => setShowForm({ student_id: s.student_id, student_name: s.student_name })} onExpediente={(s) => setExpediente(s.student_id)} />
      ) : (
        <Timeline items={timeline} onExpediente={(sid) => setExpediente(sid)} />
      )}

      {showForm && <RegisterModal students={students} preset={showForm} onClose={() => setShowForm(null)} onSaved={() => { setShowForm(null); reload(); }} />}
      {expediente && <ExpedienteModal studentId={expediente} onClose={() => setExpediente(null)} onChanged={reload} />}
    </div>
  );
}

function PesosTable({ pesos, onRegister, onExpediente }) {
  if (pesos === null) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-navy" /></div>;
  if (pesos.length === 0) return <div className="py-16 text-center text-nardo-500" data-testid="enf-pesos-empty">No hay alumnos que coincidan con el filtro.</div>;
  return (
    <div className="bg-white border border-nardo-200 rounded-xl overflow-hidden" data-testid="enf-pesos-table">
      <table className="w-full text-sm">
        <thead className="bg-nardo-50 text-nardo-500 text-[11px] uppercase tracking-wide">
          <tr><th className="text-left px-4 py-2.5">Alumno</th><th className="text-left px-4 py-2.5">Grupo</th><th className="text-left px-4 py-2.5">Peso más reciente</th><th className="text-left px-4 py-2.5">Fecha</th><th className="px-4 py-2.5"></th></tr>
        </thead>
        <tbody>
          {pesos.map((s) => (
            <tr key={s.student_id} className="border-t border-nardo-100 hover:bg-nardo-50/50" data-testid={`enf-peso-row-${s.student_id}`}>
              <td className="px-4 py-2.5 text-navy font-medium">{s.student_name}</td>
              <td className="px-4 py-2.5 text-nardo-600">{s.group_label}</td>
              <td className="px-4 py-2.5">{s.weight != null ? <span className="font-semibold text-rose-600 tabular-nums">{s.weight} kg</span> : <span className="text-nardo-400">Sin registro</span>}</td>
              <td className="px-4 py-2.5 text-nardo-500 tabular-nums">{s.weight_date || "—"}</td>
              <td className="px-4 py-2.5 text-right whitespace-nowrap">
                <button onClick={() => onExpediente(s)} data-testid={`enf-exp-${s.student_id}`} className="text-xs text-navy hover:underline mr-3">Expediente</button>
                <button onClick={() => onRegister(s)} data-testid={`enf-reg-${s.student_id}`} className="text-xs inline-flex items-center gap-1 border border-rose-300 text-rose-600 hover:bg-rose-50 px-2.5 py-1 rounded-lg font-semibold"><Plus className="w-3 h-3" /> Registrar</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Timeline({ items, onExpediente }) {
  const openBlob = async (url) => {
    try { const r = await api.get(url, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); }
    catch { toast.error("No se pudo abrir el adjunto"); }
  };
  const openJus = (id, name) => downloadPdf(`/direccion/justifications/${id}/pdf`, { filename: `justificante_${(name || "alumno").replace(/\s+/g, "_")}.pdf` });
  if (items === null) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-navy" /></div>;
  if (items.length === 0) return <div className="py-16 text-center text-nardo-500" data-testid="enf-timeline-empty">No hay actividad para el filtro seleccionado.</div>;
  return (
    <div className="space-y-2" data-testid="enf-timeline">
      {items.map((it) => {
        const meta = KIND_META[it.kind] || KIND_META.enfermeria;
        const Icon = meta.icon;
        return (
          <div key={`${it.kind}-${it.id}`} className="bg-white border border-nardo-200 rounded-xl p-3 flex items-start gap-3" data-testid={`enf-item-${it.id}`}>
            <span className={`shrink-0 w-9 h-9 rounded-lg border flex items-center justify-center ${meta.cls}`}><Icon className="w-4 h-4" /></span>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border ${meta.cls}`}>{meta.label}</span>
                <button onClick={() => onExpediente(it.student_id)} className="text-navy font-semibold text-sm hover:underline">{it.student_name}</button>
                <span className="text-[11px] text-nardo-400">· {it.group_label || "—"}</span>
                <span className="text-[11px] text-nardo-400 tabular-nums">· {it.date}</span>
              </div>
              <div className="text-sm text-navy mt-0.5">{it.title}</div>
              {it.detail && <div className="text-xs text-nardo-600 mt-0.5 whitespace-pre-wrap">{it.detail}</div>}
              <div className="flex flex-wrap items-center gap-3 mt-1 text-[11px] text-nardo-500">
                {it.weight != null && <span className="inline-flex items-center gap-1"><Weight className="w-3 h-3" /> {it.weight} kg</span>}
                {it.height != null && <span className="inline-flex items-center gap-1"><Ruler className="w-3 h-3" /> {it.height} m</span>}
                {it.temperature != null && <span className="inline-flex items-center gap-1"><Thermometer className="w-3 h-3" /> {it.temperature}°C</span>}
                {it.by && <span>· {it.by}</span>}
                {it.photo_token && <button onClick={() => openBlob(`/enfermeria/nurse-photo/${it.photo_token}`)} className="inline-flex items-center gap-1 text-navy hover:underline"><ImageIcon className="w-3 h-3" /> Ver adjunto</button>}
                {it.incident_photo_token && <button onClick={() => openBlob(`/incident-photo/${it.incident_photo_token}`)} className="inline-flex items-center gap-1 text-navy hover:underline"><ImageIcon className="w-3 h-3" /> Ver foto</button>}
                {it.kind === "justificante" && <button onClick={() => openJus(it.jus_id, it.student_name)} className="inline-flex items-center gap-1 text-navy hover:underline"><Paperclip className="w-3 h-3" /> Ver justificante ({it.attachments})</button>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function RegisterModal({ students, preset, onClose, onSaved }) {
  const [studentId, setStudentId] = useState(preset?.student_id || "");
  const [date, setDate] = useState(todayStr());
  const [weight, setWeight] = useState("");
  const [height, setHeight] = useState("");
  const [temperature, setTemperature] = useState("");
  const [motivo, setMotivo] = useState("");
  const [atencion, setAtencion] = useState("");
  const [observaciones, setObservaciones] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!studentId) { toast.error("Selecciona un alumno"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("student_id", studentId); fd.append("date", date);
      fd.append("weight", weight); fd.append("height", height); fd.append("temperature", temperature);
      fd.append("motivo", motivo); fd.append("atencion", atencion); fd.append("observaciones", observaciones);
      if (file) fd.append("file", file);
      await api.post("/enfermeria/records", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Atención de enfermería registrada");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo registrar"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-slate-900/80 flex items-start justify-center overflow-y-auto p-4" onClick={onClose} data-testid="enf-form-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 rounded-t-2xl bg-rose-500 text-white">
          <div className="font-display font-bold flex items-center gap-2"><HeartPulse className="w-4 h-4" /> Registrar atención de enfermería</div>
          <button onClick={onClose} className="text-white/80 hover:text-white p-1"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wide text-nardo-500 block mb-1">Alumno</label>
            <select value={studentId} onChange={(e) => setStudentId(e.target.value)} data-testid="enf-form-student" className="w-full border border-nardo-200 rounded-lg px-3 py-2 text-sm text-navy bg-white outline-none focus:border-navy">
              <option value="">Selecciona…</option>
              {students.map((s) => <option key={s.id} value={s.id}>{`${s.full_name} · ${groupLabel(s.group || s.grade)}`}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-[10px] uppercase tracking-wide text-nardo-500 block mb-1">Fecha</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="enf-form-date" className="w-full border border-nardo-200 rounded-lg px-3 py-2 text-sm text-navy outline-none focus:border-navy" /></div>
            <div><label className="text-[10px] uppercase tracking-wide text-nardo-500 block mb-1 flex items-center gap-1"><Weight className="w-3 h-3" /> Peso (kg)</label>
              <input type="number" step="0.1" value={weight} onChange={(e) => setWeight(e.target.value)} data-testid="enf-form-weight" placeholder="Ej. 32.5" className="w-full border border-nardo-200 rounded-lg px-3 py-2 text-sm text-navy outline-none focus:border-navy" /></div>
            <div><label className="text-[10px] uppercase tracking-wide text-nardo-500 block mb-1 flex items-center gap-1"><Ruler className="w-3 h-3" /> Estatura (m)</label>
              <input type="number" step="0.01" value={height} onChange={(e) => setHeight(e.target.value)} data-testid="enf-form-height" placeholder="Ej. 1.28" className="w-full border border-nardo-200 rounded-lg px-3 py-2 text-sm text-navy outline-none focus:border-navy" /></div>
            <div><label className="text-[10px] uppercase tracking-wide text-nardo-500 block mb-1 flex items-center gap-1"><Thermometer className="w-3 h-3" /> Temperatura (°C)</label>
              <input type="number" step="0.1" value={temperature} onChange={(e) => setTemperature(e.target.value)} data-testid="enf-form-temp" placeholder="Ej. 36.6" className="w-full border border-nardo-200 rounded-lg px-3 py-2 text-sm text-navy outline-none focus:border-navy" /></div>
          </div>
          <div><label className="text-[10px] uppercase tracking-wide text-nardo-500 block mb-1">Motivo / síntomas</label>
            <input value={motivo} onChange={(e) => setMotivo(e.target.value)} data-testid="enf-form-motivo" placeholder="Ej. Dolor de cabeza, caída, fiebre…" className="w-full border border-nardo-200 rounded-lg px-3 py-2 text-sm text-navy outline-none focus:border-navy" /></div>
          <div><label className="text-[10px] uppercase tracking-wide text-nardo-500 block mb-1">Atención brindada</label>
            <textarea value={atencion} onChange={(e) => setAtencion(e.target.value)} rows={2} data-testid="enf-form-atencion" placeholder="Qué se hizo…" className="w-full border border-nardo-200 rounded-lg px-3 py-2 text-sm text-navy outline-none focus:border-navy resize-none" /></div>
          <div><label className="text-[10px] uppercase tracking-wide text-nardo-500 block mb-1">Observaciones</label>
            <textarea value={observaciones} onChange={(e) => setObservaciones(e.target.value)} rows={2} data-testid="enf-form-obs" className="w-full border border-nardo-200 rounded-lg px-3 py-2 text-sm text-navy outline-none focus:border-navy resize-none" /></div>
          <div><label className="text-[10px] uppercase tracking-wide text-nardo-500 block mb-1">Foto / adjunto (opcional)</label>
            <input type="file" accept="image/*,application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="enf-form-file" className="w-full text-sm text-nardo-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border file:border-nardo-200 file:bg-nardo-50 file:text-navy file:text-xs file:cursor-pointer" /></div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-nardo-200">
          <button onClick={onClose} className="text-sm px-4 py-2 rounded-lg text-nardo-600 hover:bg-nardo-100">Cancelar</button>
          <button onClick={save} disabled={saving} data-testid="enf-form-save" className="text-sm px-4 py-2 rounded-lg bg-rose-500 hover:bg-rose-600 text-white font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <HeartPulse className="w-4 h-4" />} Guardar</button>
        </div>
      </div>
    </div>
  );
}

function StudentPhoto({ id, has }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!has) return undefined;
    let url;
    api.get(`/enfermeria/student/${id}/photo`, { responseType: "blob" }).then((r) => { url = URL.createObjectURL(r.data); setSrc(url); }).catch(() => {});
    return () => url && URL.revokeObjectURL(url);
  }, [id, has]);
  return src ? <img src={src} alt="" className="w-20 h-20 rounded-xl object-cover border-2 border-white shadow" data-testid="enf-student-photo" />
    : <span className="w-20 h-20 rounded-xl bg-white border border-nardo-200 flex items-center justify-center text-nardo-300" data-testid="enf-student-nophoto"><User className="w-8 h-8" /></span>;
}

function ExpedienteModal({ studentId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [profile, setProfile] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);
  const [period, setPeriod] = useState("all");
  const [sharing, setSharing] = useState(false);
  const load = useCallback(() => {
    setData(null);
    api.get(`/enfermeria/student/${studentId}`).then((r) => { setData(r.data); setProfile(r.data.medical_profile || {}); })
      .catch(() => { toast.error("No se pudo cargar el expediente"); onClose(); });
  }, [studentId, onClose]);
  useEffect(() => { load(); }, [load]);

  const saveProfile = async () => {
    setSavingProfile(true);
    try { await api.put(`/enfermeria/student/${studentId}/profile`, profile); toast.success("Ficha médica guardada"); }
    catch { toast.error("No se pudo guardar la ficha"); }
    finally { setSavingProfile(false); }
  };
  const downloadExpediente = () => downloadPdf(`/enfermeria/student/${studentId}/expediente.pdf`, {
    params: { period }, filename: `expediente_${(data?.student?.full_name || "alumno").replace(/\s+/g, "_")}.pdf`,
  });
  const shareWhatsApp = async () => {
    setSharing(true);
    try {
      const { data: s } = await api.post(`/enfermeria/student/${studentId}/share`, null, { params: { period } });
      const url = `${API}${s.path}`;
      let phone = (s.tutor_whatsapp || "").replace(/\D/g, "");
      if (phone.length === 10) phone = "52" + phone;
      const msg = `Hola${s.tutor_name ? " " + s.tutor_name : ""}, le compartimos el expediente médico de ${s.student_name} del colegio: ${url}`;
      const wa = phone ? `https://wa.me/${phone}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`;
      window.open(wa, "_blank");
      if (!phone) toast.info("El tutor no tiene WhatsApp registrado; elige el contacto manualmente.");
    } catch { toast.error("No se pudo generar el enlace"); }
    finally { setSharing(false); }
  };

  const openBlob = async (url) => {
    try { const r = await api.get(url, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); }
    catch { toast.error("No se pudo abrir el adjunto"); }
  };
  const openJus = (id, name) => downloadPdf(`/direccion/justifications/${id}/pdf`, { filename: `justificante_${(name || "alumno").replace(/\s+/g, "_")}.pdf` });
  const delRecord = async (id) => {
    if (!window.confirm("¿Eliminar este registro de enfermería?")) return;
    try { await api.delete(`/enfermeria/records/${id}`); toast.success("Registro eliminado"); load(); onChanged && onChanged(); }
    catch { toast.error("No se pudo eliminar"); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-slate-900/80 flex items-start justify-center overflow-y-auto p-4" onClick={onClose} data-testid="enf-expediente-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 rounded-t-2xl bg-navy text-white">
          <div className="font-display font-bold flex items-center gap-2"><HeartPulse className="w-4 h-4" /> Expediente médico {data?.student ? `· ${data.student.full_name}` : ""}</div>
          <button onClick={onClose} className="text-white/80 hover:text-white p-1"><X className="w-5 h-5" /></button>
        </div>
        {!data ? <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-navy" /></div> : (
          <div className="p-5 space-y-5 max-h-[75vh] overflow-y-auto">
            <div className="flex flex-wrap gap-4 items-center bg-nardo-50 rounded-xl p-3">
              <StudentPhoto id={data.student.id} has={data.student.has_photo} />
              <span className="text-sm text-navy font-semibold flex items-center gap-1.5"><User className="w-4 h-4 text-navy-light" /> {data.student.full_name}</span>
              <span className="text-xs text-nardo-500">{data.student.group_label}</span>
              {data.weight_history.length > 0 && <span className="text-xs text-rose-600 font-semibold flex items-center gap-1"><Weight className="w-3.5 h-3.5" /> Último peso: {data.weight_history[data.weight_history.length - 1].weight} kg</span>}
            </div>

            <div className="flex flex-wrap items-center gap-2 bg-navy/5 rounded-xl p-3" data-testid="enf-exp-toolbar">
              <span className="text-[11px] uppercase tracking-wider text-nardo-500">Periodo:</span>
              <select value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="enf-exp-period" className="text-xs border border-nardo-200 rounded-lg px-2 py-1.5 bg-white text-navy outline-none focus:border-navy">
                {PERIODS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                <option value="year">Ciclo escolar</option>
              </select>
              <button onClick={downloadExpediente} data-testid="enf-exp-download" className="ml-auto text-xs inline-flex items-center gap-1.5 bg-navy hover:bg-navy-light text-white px-3 py-1.5 rounded-lg font-semibold"><Download className="w-3.5 h-3.5" /> Descargar PDF</button>
              <button onClick={shareWhatsApp} disabled={sharing} data-testid="enf-exp-whatsapp" className="text-xs inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg font-semibold disabled:opacity-50">{sharing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageCircle className="w-3.5 h-3.5" />} WhatsApp al tutor</button>
            </div>

            <Section title="Ficha médica" icon={HeartHandshake} count={MEDICAL_FIELDS.length}>
              <div className="grid sm:grid-cols-2 gap-3" data-testid="enf-ficha-medica">
                {MEDICAL_FIELDS.map((f) => (
                  <div key={f.k} className={f.k === "notes" ? "sm:col-span-2" : ""}>
                    <label className="text-[10px] uppercase tracking-wider text-nardo-500 block mb-0.5">{f.label}</label>
                    {f.k === "notes" ? (
                      <textarea value={profile[f.k] || ""} onChange={(e) => setProfile((p) => ({ ...p, [f.k]: e.target.value }))} data-testid={`enf-ficha-${f.k}`} rows={2} className="w-full border border-nardo-200 rounded-lg px-2.5 py-1.5 text-sm outline-none focus:border-navy resize-none" />
                    ) : (
                      <input value={profile[f.k] || ""} onChange={(e) => setProfile((p) => ({ ...p, [f.k]: e.target.value }))} data-testid={`enf-ficha-${f.k}`} className="w-full border border-nardo-200 rounded-lg px-2.5 py-1.5 text-sm outline-none focus:border-navy" />
                    )}
                  </div>
                ))}
              </div>
              <button onClick={saveProfile} disabled={savingProfile} data-testid="enf-ficha-save" className="mt-2 text-xs inline-flex items-center gap-1.5 bg-rose-600 hover:bg-rose-700 text-white px-4 py-2 rounded-lg font-semibold disabled:opacity-50">{savingProfile ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Guardar ficha médica</button>
            </Section>

            <Section title="Atenciones de enfermería" icon={Stethoscope} count={data.records.length}>
              {data.records.length === 0 ? <Empty /> : data.records.map((r) => (
                <div key={r.id} className="border border-nardo-200 rounded-lg p-3" data-testid={`enf-exp-rec-${r.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-navy">{r.date} · {r.motivo || "Atención"}</span>
                    <button onClick={() => delRecord(r.id)} className="text-nardo-400 hover:text-red-600" data-testid={`enf-exp-del-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                  <div className="flex flex-wrap gap-3 mt-1 text-[11px] text-nardo-600">
                    {r.weight != null && <span className="inline-flex items-center gap-1"><Weight className="w-3 h-3" /> {r.weight} kg</span>}
                    {r.height != null && <span className="inline-flex items-center gap-1"><Ruler className="w-3 h-3" /> {r.height} m</span>}
                    {r.temperature != null && <span className="inline-flex items-center gap-1"><Thermometer className="w-3 h-3" /> {r.temperature}°C</span>}
                    {r.created_by_name && <span>· {r.created_by_name}</span>}
                  </div>
                  {r.atencion && <div className="text-xs text-navy mt-1"><b>Atención:</b> {r.atencion}</div>}
                  {r.observaciones && <div className="text-xs text-nardo-600 mt-0.5">{r.observaciones}</div>}
                  {r.photo_token && <button onClick={() => openBlob(`/enfermeria/nurse-photo/${r.photo_token}`)} className="text-xs text-navy hover:underline inline-flex items-center gap-1 mt-1"><ImageIcon className="w-3 h-3" /> Ver adjunto</button>}
                </div>
              ))}
            </Section>

            <Section title="Bitácora de docentes (incidencias)" icon={AlertTriangle} count={data.incidents.length}>
              {data.incidents.length === 0 ? <Empty /> : data.incidents.map((i) => (
                <div key={i.id} className="border border-nardo-200 rounded-lg p-3" data-testid={`enf-exp-inc-${i.id}`}>
                  <div className="text-sm font-semibold text-navy">{(i.created_at || "").slice(0, 10)} · {i.type} <span className="text-[10px] uppercase text-amber-700">({i.level})</span></div>
                  {i.description && <div className="text-xs text-nardo-600 mt-0.5 whitespace-pre-wrap">{i.description}</div>}
                  <div className="text-[11px] text-nardo-500 mt-0.5">{i.created_by_name}{i.photo_token && <> · <button onClick={() => openBlob(`/incident-photo/${i.photo_token}`)} className="text-navy hover:underline">Ver foto</button></>}</div>
                </div>
              ))}
            </Section>

            <Section title="Justificantes médicos" icon={FileCheck2} count={data.justificantes.length}>
              {data.justificantes.length === 0 ? <Empty /> : data.justificantes.map((j) => (
                <div key={j.id} className="border border-nardo-200 rounded-lg p-3 flex items-center justify-between" data-testid={`enf-exp-jus-${j.id}`}>
                  <div><div className="text-sm font-semibold text-navy">{j.date} · {j.reason}</div>
                    <div className="text-[11px] text-nardo-500">{j.attachments.length} adjunto(s)</div></div>
                  <button onClick={() => openJus(j.id, data.student.full_name)} className="text-xs inline-flex items-center gap-1 border border-navy/30 text-navy hover:bg-navy hover:text-white px-2.5 py-1 rounded-lg font-semibold"><Paperclip className="w-3 h-3" /> Ver</button>
                </div>
              ))}
            </Section>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, count, children }) {
  return (
    <div>
      <h3 className="text-sm font-display font-bold text-navy mb-2 flex items-center gap-2"><Icon className="w-4 h-4 text-navy-light" /> {title} <span className="text-[11px] font-normal text-nardo-400">({count})</span></h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

const Empty = () => <div className="text-xs text-nardo-400 py-2">Sin registros.</div>;
